import React from 'react';
import { motion } from 'framer-motion';
import { Search, Code, CheckCircle, ArrowRight, ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Services.css';

const Services = () => {
  const offers = [
    {
      icon: <Search size={28} />,
      name: 'QA Automation Audit',
      price: '$300–$500',
      timeline: 'Delivered in 2–5 hours',
      description:
        'A focused review of your current test coverage, tooling, and automation gaps. You walk away with a concrete action plan — not a vague report.',
      bullets: [
        'Review of existing tests and CI setup',
        'Identification of highest-risk coverage gaps',
        'Prioritized recommendations with effort estimates',
        'Written summary you can hand to your dev team',
      ],
      cta: 'Book an Audit',
      accent: 'purple',
    },
    {
      icon: <Code size={28} />,
      name: 'Playwright Smoke-Test Starter',
      price: '~$1,000',
      timeline: 'Delivered in ~1 week',
      description:
        'A working Playwright test suite covering your most critical user flows, wired into your CI pipeline and ready to run on every deploy.',
      bullets: [
        'Smoke tests for your core happy-path flows',
        'Playwright setup + CI/CD integration',
        'Page Object Model structure for easy extension',
        'Handoff walkthrough so your team can own it',
      ],
      cta: 'Get a Starter Suite',
      accent: 'cyan',
    },
  ];

  return (
    <section className="services" id="services">
      <div className="container">
        <motion.div
          className="services-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <span className="section-label">What I Offer</span>
          <h2>Two Ways to Get Started</h2>
          <p>
            Both offers are fixed-scope, fixed-price, and designed to deliver
            real value fast — no retainer required.
          </p>
        </motion.div>

        <div className="services-grid">
          {offers.map((offer, i) => (
            <motion.div
              key={i}
              className={`service-card service-card--${offer.accent}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              viewport={{ once: true }}
            >
              <div className="service-card-inner">
                <div className={`service-icon service-icon--${offer.accent}`}>
                  {offer.icon}
                </div>
                <h3 className="service-name">{offer.name}</h3>
                <div className="service-pricing">
                  <span className="service-price">{offer.price}</span>
                  <span className="service-timeline">{offer.timeline}</span>
                </div>
                <p className="service-description">{offer.description}</p>
                <ul className="service-bullets">
                  {offer.bullets.map((b, j) => (
                    <li key={j}>
                      <CheckCircle size={16} className="bullet-icon" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href={`mailto:mrichard.hudson@mixality.com?subject=${encodeURIComponent(offer.cta)}`}
                  className={`btn service-btn service-btn--${offer.accent}`}
                >
                  {offer.cta}
                  <ArrowRight size={18} />
                </a>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="services-assessment-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <div className="assessment-cta-inner">
            <div className="assessment-cta-icon">
              <ClipboardList size={24} />
            </div>
            <div className="assessment-cta-text">
              <strong>Not sure where you stand?</strong>
              <span>Take the free 5-minute QA self-assessment and get a scored breakdown of your coverage gaps.</span>
            </div>
            <Link to="/audit" className="btn btn-secondary assessment-cta-btn">
              Take the Free Self-Assessment <ArrowRight size={18} />
            </Link>
          </div>
        </motion.div>

        <motion.p
          className="services-note"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
        >
          Not sure which fits? Book a free 20-minute discovery call and we'll
          figure it out together.{' '}
          <a href="mailto:mrichard.hudson@mixality.com?subject=Free Discovery Call">
            Get in touch →
          </a>
        </motion.p>
      </div>
    </section>
  );
};

export default Services;
